/** @jsx isolateComponent */
import { useState } from 'react';
import { css } from '@emotion/core';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors } from '../../../theme/Colors';
import { useOverflow } from '../../../hooks/useOverflow';

type Props = {
  /**
   * The content to render for the description.
   */
  children: React.ReactNode;
  /**
   * The maximum number of lines to render before truncating.
   */
  maxLines?: number;
};

const Styles = labelStyles('AlertDescription', {
  root: css`
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  `,
  content: (props: Props, { isOpen }: { isOpen: boolean }) => [
    css`
      // All of the properties below this are necessary
      // for line clamping to work. Even though the properties
      // have 'webkit' in them, they work cross-browser.
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: ${props.maxLines};
      overflow: hidden;

      > * {
        display: inline;
      }
    `,
    isOpen &&
      css`
        label: -open;
        -webkit-line-clamp: initial;
      `,
  ],
  toggle: css`
    cursor: pointer;
    textTransform: lowercase;
    color: ${Colors.blue500};

    &:hover, &:focus: {
      text-decoration: underline;
    },
  `,
});

const defaultProps = {
  maxLines: 3,
};

/**
 * `AlertDescription` renders the description for an `Alert`.
 */
export const AlertDescription = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowing, setOverflowingNode] = useOverflow();
  // Only render toggle if it's overflowing.
  const toggle = isOverflowing && (
    <button
      aria-hidden="true"
      css={Styles.toggle}
      onClick={() => setIsOpen(!isOpen)}
      type="button"
    >
      {isOpen ? 'show less' : 'show more'}
    </button>
  );
  return (
    <div css={Styles.root}>
      <div css={Styles.content(props, { isOpen })} ref={setOverflowingNode}>
        <p>{props.children}</p>
      </div>
      {toggle}
    </div>
  );
};
